import React, { useEffect } from 'react';
import { ReactComponent as LoadingSpinner } from 'images/loader.svg';
import { ReactComponent as CheckIcon } from 'images/check-with-confetti.svg';
import { useDispatch, useSelector } from 'react-redux';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { useQueryParams } from 'hooks/useQueryParam';
import { getPaycheckSwitchOffer, joinCardWaitList } from 'thunks';
import { setCardData } from 'handlers/cardData';
import { trackConversionLead } from 'utils/analytics';
import { identifyPostHogUser } from 'utils/posthog';
import { getUtmTagsVariables } from 'utils/getUtmTags';
import StateContainer from 'components/StateContainer';
import { getYourContactData } from 'selectors/getYourContact';
import { getYourNameData } from 'selectors/yourName';
import { getYourIncome } from 'selectors/yourIncome';
import { getCardData } from 'selectors/getCardData';
import { FlowComponentType } from 'routes/types';
import { ApplyingResult as Result } from 'enums/FlowNextResults';
import { useNavigationType } from 'hooks/useNavigate';
import { getAuthData } from 'selectors/getAuthData';
import { getProfessionGroup } from 'selectors/professionGroup';
import { getPreQualificationData } from 'selectors/preQualificationData';
import { getClientTimezone } from 'utils/dateUtils';
import RequestError from 'errors/RequestError';

import styles from './Applying.module.scss';

const Applying = ({ handleNext }: FlowComponentType): JSX.Element => {
  const params = useQueryParams();
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigationType = useNavigationType();

  const { email, phone_number: phoneNumber } = useSelector(getYourContactData);
  const { first_name: firstName, last_name: lastName } = useSelector(getYourNameData);
  const {
    not_employed: notEmployed,
    total_annual_income: totalAnnualIncome,
    start_of_employment: startOfEmployment,
    employer_name: employerName,
    credit_score_range: creditScoreRange,
  } = useSelector(getYourIncome);

  const { professionGroup } = useSelector(getProfessionGroup);
  const {
    cardColor,
    borrowerCredentials,
    initialReferrer,
    referredBy,
    applied,
    smartSpendingLimitsEnabled,
    smartAutoPayEnabled,
    importDebtEnabled,
  } = useSelector(getCardData);
  const { partnerId: partnerName } = useSelector(getPreQualificationData);
  const { sessionToken } = useSelector(getAuthData);

  useEffect(() => {
    const apply = async () => {
      let cardAppliedResponse;
      try {
        cardAppliedResponse = await dispatchWithUnwrap(
          joinCardWaitList({
            firstName,
            lastName,
            professionGroup,
            credentials: borrowerCredentials!,
            email,
            phoneNumber,
            totalAnnualIncome: totalAnnualIncome ?? undefined,
            notEmployed,
            employment: [
              {
                employerName,
                hireDate: startOfEmployment ?? undefined,
              },
            ],
            creditScoreRange: creditScoreRange ?? undefined,
            cardColor: cardColor!,
            utm: getUtmTagsVariables(params),
            httpReferrer: initialReferrer,
            cardReferredBy: referredBy,
            timezone: getClientTimezone(),
            resumeLink: `${window.location.href}`,
            partnerId: partnerName,
            smartSpendingLimitsEnabled,
            smartAutoPayEnabled,
            importDebtEnabled,
            sessionToken,
          }),
        );
      } catch (error) {
        if ((error as RequestError<unknown>).responseStatus === 401) {
          handleNext(Result.SessionExpired);
        } else {
          handleNext(Result.Error);
        }
        return;
      }

      // Student Loan Assistance Data
      // Is fetched in the background while running cross sell (We need the loan application id to fetch it)

      await dispatchWithUnwrap(getPaycheckSwitchOffer(cardAppliedResponse.applicationId!));

      if (cardAppliedResponse.applied) {
        dispatch(setCardData(cardAppliedResponse));
        handleNext(Result.Done);
      }

      if (cardAppliedResponse.borrowerId) {
        analytics.identify(cardAppliedResponse.borrowerId);
        identifyPostHogUser(cardAppliedResponse.borrowerId, { email, role: borrowerCredentials });
        (window as any).nid('setUserId', cardAppliedResponse.borrowerId);
      }

      trackConversionLead({
        email,
        firstName,
        lastName,
        phoneNumber,
      });
    };

    if (navigationType === 'POP' && applied) {
      return;
    }
    apply();
  }, []);

  return (
    <div className={styles.container}>
      {navigationType === 'POP' && applied ? (
        <StateContainer icon={<CheckIcon />} title="Joined" />
      ) : (
        <StateContainer icon={<LoadingSpinner />} title="Joining..." />
      )}
    </div>
  );
};

export default Applying;
